"use client";
import { useState, useEffect } from "react";
import cn from "@/utils/cn";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  CollapseSideNav,
  EAELogo,
  ExpandSideNav,
} from "@/assets/svg-icons/svg-icons";
import { useZStore } from "@/utils/z-store";
import { hasActivePremium } from "@/utils/subscription-utils";
import { useWorkspacePermissions } from "@/app/(dashboard)/dashboard/_hooks/useWorkspacePermissions";
import type { Capability } from "@/shared/workspace-permissions";
import TextComponent from "./TextComponent";
import PremiumCardUpgrade from "./modals/PremiumCardUpgrade";
import BrandWordmark from "./BrandWordmark";

type NavItem = {
  name: string;
  route: string;
  defaultIcon: any;
  activeIcon: any;
  isDisabled?: boolean;
  isIcon?: boolean;
  // If set, the nav item is only shown to roles that hold this capability in
  // the active workspace (§WS3). Items without one are visible to everyone.
  capability?: Capability;
};

type SideNavProps = {
  navs: NavItem[];
  className?: string;
};

const SideNav = ({ navs, className = "" }: SideNavProps) => {
  const pathname = usePathname();
  const { data } = useZStore();
  // Show each role only the pages it can act on in the active workspace (§WS3).
  // Until the active role resolves (role === null on first paint) we show every
  // item, so admins never see nav links flash out; the store is persisted so
  // this resolves near-instantly.
  const { role, can } = useWorkspacePermissions();
  const visibleNavs = role
    ? navs.filter((nav) => !nav.capability || can(nav.capability))
    : navs;
  const [shouldRender, setShouldRender] = useState(false);
  const [collapseSideNav, setCollapseSideNav] = useState(true); // State for collapse
  const [screenHeight, setScreenHeight] = useState(0);

  useEffect(() => {
    setShouldRender(data.sideBarVisible);
  }, [data.sideBarVisible]);

  // Add new useEffect for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setScreenHeight(window.innerHeight);

      if (window.innerWidth >= 1920) {
        setCollapseSideNav(false);
      } else {
        setCollapseSideNav(true);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const hiddenSideNav = ["edit", "form-builder", "download-report"];
  const route = pathname.split("/")[2];

  // Determine if we should show the premium upgrade
  const shouldShowPremiumUpgrade =
    screenHeight > 712 &&
    !collapseSideNav &&
    (!hasActivePremium(data.subscriptionPlan, data.subscriptionStatus) ||
      data.subscriptionPlan?.toUpperCase().includes("TRIAL_PREMIUM"));

  if (hiddenSideNav.includes(route) || !shouldRender) return null;

  // Function to toggle collapse state
  const toggleCollapse = () => {
    setCollapseSideNav((prev) => !prev);
  };

  return (
    <>
      {/* Overlay */}
      {!collapseSideNav && window.innerWidth < 1920 && (
        <div
          className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-300"
          onClick={() => setCollapseSideNav(true)}
        />
      )}

      <div className="">
        <div
          className={cn(
            "h-full text-black shadow-lg",
            // "transition-all duration-300 ease-in-out",
            "opacity-100 translate-x-0",
            "flex flex-col justify-between w-fit",
            collapseSideNav
              ? "relative w-[75px] xl:w-[103px] px-[14px] xl:px-5 bg-white"
              : "2xl:relative fixed w-[258px] xl:w-[345px] px-[34px] xl:px-[46px] bg-white",
            "z-50",
            className,
          )}
        >
          <div className="">
            <Logo collapseSideNav={collapseSideNav} />
            <NavList
              navs={visibleNavs}
              pathname={pathname}
              modalName={data.modal.modalName || ""}
              collapseSideNav={collapseSideNav} // Pass collapse state
            />
          </div>

          <div
            className={cn(
              "flex flex-col gap-[20px] mb-10",
              collapseSideNav ? "items-center" : "items-stretch",
            )}
          >
            {shouldShowPremiumUpgrade && <PremiumCardUpgrade />}

            <button
              type="button"
              onClick={toggleCollapse}
              aria-label={collapseSideNav ? "Expand menu" : "Collapse menu"}
              className={cn(
                "flex size-[45px] items-center justify-center rounded-[12px] border border-kd-secondary-6 bg-white text-kd-secondary-2 transition-colors hover:bg-kd-secondary-6 hover:text-kd-secondary-1",
                !collapseSideNav && "self-end",
              )}
            >
              {!collapseSideNav ? (
                <CollapseSideNav className="size-[23.25px]" />
              ) : (
                <ExpandSideNav className="size-[23.25px]" />
              )}
            </button>
          </div>
        </div>
        <div
          className={cn(
            "h-full  bg-black/10",
            collapseSideNav ? "hidden" : "w-[75px] xl:w-[103px]",
          )}
        ></div>
      </div>
    </>
  );
};

const Logo = ({ collapseSideNav }: { collapseSideNav: boolean }) => {
  const pathname = usePathname();
  const { data } = useZStore();
  const brandIconUrl = data?.brandIcon;

  const handleLogoClick = () => {
    if (pathname === "/") {
      window.location.reload();
    } else {
      window.location.href = "/";
    }
  };

  return (
    <div
      onClick={handleLogoClick}
      className={cn(
        "w-full flex justify-center items-center max-xl:mb-6 2xl:mb-[35px] h-20 cursor-pointer xl:h-[120px]",
      )}
    >
      {!collapseSideNav ? (
        <BrandWordmark className="w-[190px] xl:w-[230px]" />
      ) : (
        <div className="relative flex h-full items-center justify-center">
          {brandIconUrl ? (
            // <Image
            //   alt="Brand Icon"
            //   src={brandIconUrl}
            //   width={120}
            //   height={120}
            //   className="max-h-[120px] w-auto object-contain"
            // />
            <EAELogo className="h-[30px] w-auto xl:h-[40px]" />
          ) : (
            <EAELogo className="h-[30px] w-auto xl:h-[40px]" />
          )}
        </div>
      )}
    </div>
  );
};

const NavList = ({
  navs,
  pathname,
  modalName,
  collapseSideNav,
}: {
  navs: NavItem[];
  pathname: string;
  modalName: string;
  collapseSideNav: boolean;
}) => (
  <ul
    className={cn(
      !collapseSideNav ? "space-y-[10px]" : "space-y-[4px]",
    )}
  >
    {navs.map((nav, index) => (
      <NavItemComponent
        key={index}
        nav={nav}
        pathname={pathname}
        modalName={modalName}
        collapseSideNav={collapseSideNav} // Pass collapse state
      />
    ))}
  </ul>
);

// Match a nav item to the current route by its top-level section (the first two
// path segments, e.g. "dashboard/settings"), not the item's exact default route.
// This keeps "Settings" highlighted across all its sub-pages
// (/dashboard/settings/workspace, /users, …) instead of only /settings/profile.
const sectionOf = (path: string) => path.split("/").slice(1, 3).join("/");
const isNavActive = (pathname: string, route: string) =>
  sectionOf(pathname) === sectionOf(route);

const NavItemComponent = ({
  nav,
  pathname,
  modalName,
  collapseSideNav,
}: {
  nav: NavItem;
  pathname: string;
  modalName: string;
  collapseSideNav: boolean;
}) => {
  const { name, route, activeIcon, defaultIcon, isDisabled } = nav;

  const isActive = isNavActive(pathname, route);

  const isLinkDisabled =
    nav.name !== "Dashboard" &&
    (isDisabled ||
      modalName === "mailchimp-api-key" ||
      modalName === "dashboard-welcome");

  return (
    <div className={cn(
      "group relative flex flex-col items-center",
      !collapseSideNav && "w-full"
    )}>
      <Link
        href={isLinkDisabled ? "#" : route}
        title={name}
        className={cn(
          "flex items-center transition-all duration-150 ease-in-out",
          collapseSideNav
            ? "justify-center size-[45px] xl:size-[45px] rounded-[12px]"
            : "w-full justify-start p-[11px] gap-[11.25px] rounded-[12px]",
          {
            "bg-kd-secondary-6": isActive,
            "group-hover:bg-kd-secondary-6": !isActive && !isLinkDisabled,
            "opacity-50 cursor-not-allowed text-[#979797]": isLinkDisabled,
          },
          "relative overflow-hidden",
        )}
      >
        <NavIcon
          defaultIcon={defaultIcon}
          activeIcon={activeIcon}
          isActive={isActive}
          isIcon={nav.isIcon || false}
          isDisabled={isLinkDisabled}
        />
        {!collapseSideNav && (
          <NavText
            name={name}
            isActive={isActive}
            isDisabled={isLinkDisabled}
          />
        )}
      </Link>
      {isDisabled && <ComingSoonTooltip />}
    </div>
  );
};

const NavIcon = ({
  defaultIcon,
  activeIcon,
  isActive,
  isIcon = false,
  isDisabled = false,
}: {
  defaultIcon: any;
  activeIcon: any;
  isActive: boolean;
  isIcon: boolean;
  isDisabled?: boolean;
}) => (
  <div
    className={cn(
      "relative shrink-0 size-[23.25px]",
      !isActive && !isDisabled && "text-kd-secondary-2 group-hover:text-kd-secondary-1",
      isActive && "text-kd-lte-blue",
    )}
  >
    {isIcon ? (
      <>
        <div
          className={cn(
            "absolute inset-0 transition-opacity duration-150 ease-in-out",
            isActive
              ? "opacity-0"
              : isDisabled
                ? "opacity-100"
                : "opacity-100 group-hover:opacity-0",
          )}
        >
          {defaultIcon}
        </div>
        {activeIcon && (
          <div
            className={cn(
              "absolute inset-0 transition-opacity duration-150 ease-in-out",
              isActive
                ? "opacity-100"
                : isDisabled
                  ? "opacity-0"
                  : "opacity-0 group-hover:opacity-100",
            )}
          >
            {activeIcon}
          </div>
        )}
      </>
    ) : (
      <>
        <div
          className={cn(
            "absolute inset-0 bg-current transition-opacity duration-150 ease-in-out",
            isActive
              ? "opacity-0"
              : isDisabled
                ? "opacity-100"
                : "opacity-100 group-hover:opacity-0",
          )}
          style={{
            maskImage: `url(${typeof defaultIcon === "string" ? defaultIcon : defaultIcon.src})`,
            maskSize: "contain",
            maskRepeat: "no-repeat",
            maskPosition: "center",
            WebkitMaskImage: `url(${typeof defaultIcon === "string" ? defaultIcon : defaultIcon.src})`,
            WebkitMaskSize: "contain",
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
          }}
        />
        {activeIcon && (
          <div
            className={cn(
              "absolute inset-0 bg-current transition-opacity duration-150 ease-in-out",
              isActive
                ? "opacity-100"
                : isDisabled
                  ? "opacity-0"
                  : "opacity-0 group-hover:opacity-100",
            )}
            style={{
              maskImage: `url(${typeof activeIcon === "string" ? activeIcon : activeIcon.src})`,
              maskSize: "contain",
              maskRepeat: "no-repeat",
              maskPosition: "center",
              WebkitMaskImage: `url(${typeof activeIcon === "string" ? activeIcon : activeIcon.src})`,
              WebkitMaskSize: "contain",
              WebkitMaskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
            }}
          />
        )}
      </>
    )}
  </div>
);

const NavText = ({
  name,
  isActive,
  isDisabled,
}: {
  name: string;
  isActive: boolean;
  isDisabled?: boolean;
}) => (
  <TextComponent
    type="h3"
    className={cn(
      "md:flex transition-all duration-150 ease-in-out text-[16px] xl:text-[16px] leading-normal text-kd-secondary-2",
      {
        "text-kd-lte-blue font-medium": isActive,
        "font-normal group-hover:text-kd-secondary-1": !isActive && !isDisabled,
        "text-inherit cursor-not-allowed font-normal": isDisabled,
      },
    )}
  >
    {name}
  </TextComponent>
);

const ComingSoonTooltip = () => (
  <div className="absolute bottom-full left-7 hidden text-nowrap group-hover:flex">
    <span className="relative rounded-lg bg-kd-lte-blue px-2 py-1 text-xs text-white">
      Coming Soon
      <span className="absolute left-[18px] top-full size-0 -translate-x-1/2 border-x-8 border-t-8 border-x-transparent border-t-kd-lte-blue"></span>
    </span>
  </div>
);


export default SideNav;
